import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../../../../../api/generated';
import { KendoLoading } from '../../../../../components';
import ListItemCreateButton from '../../../../../components/custom/ListItemCreateButton';
import { useFetchedValue } from '../../../../../components/hooks/useFetched';
import { usePageableGrid } from '../../../../../components/hooks/usePageableGrid';
import { Grid, GridColumn } from '../../../../../components/KendoReact/Grid';
import { useWindow } from '../../../../../components/windows/WindowContext';
import { priorityLevel, PriorityLevel } from '../../../../../enums';
import AdministrationActionCell from '../../components/AdministrationActionCell';
import StqlCreateModalWindow from './StqlCreateModalWindow';
import StqlEditModalWindow from './StqlEditModalWindow';

const getServiceTerminalQueueLocationsByQueue = async (queueId, skip: number, take: number) =>
    api.servicesTerminalsQueuesLocations.getServiceTerminalQueueLocationsByQueue({
        queueId,
        skip,
        take
    });

export interface IServiceTerminalQueueLocation {
    id: number;
    queueId: number;
    locationId: number;
    terminalServiceId: number;
    priorityLevel: PriorityLevel;
}

export interface IServiceTerminalQueueLocationListItem extends IServiceTerminalQueueLocation {
    serviceName: string | null;
    terminalName: string | null;
    locationName: string | null;
}

interface IProps {
    fetchData: () => Promise<void>;
    editableQueueId: number;
    organizationInformationId: number;
}
const StqlList: FC<IProps> = (props) => {
    const [gridPageProps] = usePageableGrid([5, 10, 15, 20, 25], 15);
    const { openWindow } = useWindow();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [stqlItems, refetchStqlItems, , , isStqlItemsLoading] = useFetchedValue(
        getServiceTerminalQueueLocationsByQueue,
        true,
        props.editableQueueId,
        gridPageProps.skip,
        gridPageProps.take
    );

    const handleApprove = async (id: number) => {
        try {
            setIsLoading(true);
            await api.servicesTerminalsQueuesLocations.deleteServiceTerminalQueueLocation(id);
            refetchStqlItems();
            toast.success('Eilės elementas sėkmingai ištrintas');
        } catch (ex) {
            toast.error(ex.error[0]);
        } finally {
            setIsLoading(false);
        }
    };

    const openStqlEditModalWindow = async (stqlId: number) => {
        try {
            StqlEditModalWindow.defaultProps = {
                componentProps: {
                    editableSTQLId: stqlId,
                    fetchList: refetchStqlItems,
                    organizationInformationId: props.organizationInformationId
                }
            };
            openWindow({
                title: 'Eilės redagavimas',
                component: StqlEditModalWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const openStqlCreateModalWindow = async (basedOnInitialValues: IServiceTerminalQueueLocation | null) => {
        try {
            StqlCreateModalWindow.defaultProps = {
                componentProps: {
                    queueId: props.editableQueueId,
                    organizationInformationId: props.organizationInformationId,
                    refetchStqlList: refetchStqlItems,
                    basedOnInitialValues: basedOnInitialValues,
                    openStqlCreateModalWindow: openStqlCreateModalWindow
                }
            };
            openWindow({
                title: 'Priskirti elementą eilei',
                component: StqlCreateModalWindow,
                isDialogBehaviour: true,
                width: 1200,
                height: 780
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    return (
        <>
            {isLoading ? (
                <KendoLoading />
            ) : (
                <>
                    <ListItemCreateButton openCreateModal={() => openStqlCreateModalWindow(null)} />
                    <Grid selectedField="checked" data={stqlItems} {...gridPageProps} isLoading={isStqlItemsLoading}>
                        <GridColumn
                            width={200}
                            title="Veiksmai"
                            cell={(c) => (
                                <AdministrationActionCell
                                    {...c}
                                    fetchData={refetchStqlItems}
                                    deleteItem={handleApprove}
                                    editItem={openStqlEditModalWindow}
                                />
                            )}
                        />
                        <GridColumn width={200} title="Paslauga" field="serviceName" />
                        <GridColumn width={200} title="Terminalas" field="terminalName" />
                        <GridColumn width={200} title="Lokacija" field="locationName" />
                        <GridColumn
                            title="Prioriteto lygis"
                            field="priorityLevel"
                            cell={(c) => <td> {priorityLevel.format(c.dataItem.priorityLevel)} </td>}
                        />
                    </Grid>
                </>
            )}
        </>
    );
};

export default StqlList;
